/**
 * UserService - all about our user
 * - display name, avatar etc
 * - @see auth for login/logout actions etc
 * - @see organization for orgs related stuff (list of our orgs etc)
 */
import { HttpClient } from '@angular/common/http';
import type { WritableSignal } from '@angular/core';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { Observable } from 'rxjs';
import { combineLatest, filter, firstValueFrom, map, switchMap } from 'rxjs';

import { LOADING_STATES, type LoadingState, type Maybe } from '@evc/web-components';

import { PlatformConfigService } from '../../../features/config/config.service';
import { CORE_API_ENDPOINTS } from '../../../features/config/config.type';
import type { ApiResponse } from '../../../features/types/api.type';
import { OrganizationsService } from '../organizations/organizations.service';
import { Permission } from '../types/permission.type';
import { Role } from '../types/role.type';
import { ApiWebApp } from '../types/webapp.type';
import { CoreClientUtilService } from '../utils/core-client-utils.service';
import type {
  ApiSaveUserRequest,
  ApiUser,
  ComputedWebApp,
  UserApi,
  UserProfile,
} from './user.type';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  #CoreClientUtilService = inject(CoreClientUtilService);
  /* only needed for current userRoles
  - if circular deps issues arise, you may assign userRoles in a signal here via platformService */
  #organizationsService = inject(OrganizationsService);
  private _configService = inject(PlatformConfigService);
  #httpClient = inject(HttpClient);
  #destroyRef = inject(DestroyRef);

  #state = signal<LoadingState>(LOADING_STATES.PRISTINE);
  ready = computed(() => this.#state() === LOADING_STATES.READY);
  ready$ = toObservable(this.ready).pipe(filter(Boolean), takeUntilDestroyed(this.#destroyRef));

  #profile: WritableSignal<Maybe<UserProfile>> = signal(undefined);
  // reinject user roles convenience : if useConnect roles are managed via organizationService
  profile = computed<Maybe<UserProfile>>(() => {
    const profile = this.#profile();
    if (!profile) return undefined;

    const useConnect = this._configService.useConnect();
    if (!useConnect) return profile;

    const currentOrganization = this.#organizationsService.current();
    const userRoles: Role[] = currentOrganization?.userRoles || profile.roles || [];

    return {
      ...profile,
      roles: userRoles,
    } as UserProfile;
  });

  id = computed(() => this.#profile()?.id);

  isAvatarImage = computed(() => this.profile()?.avatar?.type === 'image');

  #userApplications = signal<Maybe<ComputedWebApp[]>>(undefined);
  applications = this.#userApplications.asReadonly();

  setUser(user: Maybe<UserApi>, permissions: Permission[] = []): Maybe<UserProfile> {
    const profile = user
      ? this.#CoreClientUtilService.computeUserProfile(user, permissions)
      : undefined;
    this.#profile.set(profile);

    if (profile?.id) {
      this.#state.set(LOADING_STATES.READY);
    }

    return profile;
  }

  updateUser(data: Partial<UserApi>): UserProfile {
    this.#profile.update((currentProfile) =>
      this.#CoreClientUtilService.computeUserProfile({
        ...(currentProfile ?? {}),
        ...data,
      } as UserProfile),
    );

    return this.#profile()!;
  }

  /** fetch user profile
   * * expected to call this when aquire token (cf platform/app.component) */
  async init(): Promise<Maybe<UserProfile>> {
    // may have already been registred via <evc-platform> inputs
    if (this.#state() === LOADING_STATES.READY) {
      return this.#profile();
    }

    if (this.#state() !== LOADING_STATES.PRISTINE) {
      throw new Error('User service already initialized');
    }

    this.#state.set(LOADING_STATES.LOADING);

    return this._configService.useConnect()
      ? this.updateProfile()
      : firstValueFrom(this.ready$.pipe(map(() => this.#profile())));
  }

  clear(): void {
    this.setUser(undefined);
    this.#state.set(LOADING_STATES.PRISTINE);
  }

  /** fetch user info
   * - remap data to fit our app model
   * - then update our profile
   */
  async updateProfile(): Promise<UserProfile> {
    return firstValueFrom(combineLatest([this.#fetchUser$(), this.#fetchUserPermissions$()]))
      .then(([user, permissions]) => this.setUser(user, permissions)!)
      .catch((error) => {
        this.setUser(undefined);

        throw error;
      });
  }

  async saveUser(payload: ApiSaveUserRequest): Promise<UserProfile> {
    await firstValueFrom(this.#saveUser$(payload));
    const currentAvatar = this.#profile()?.avatar;
    return this.updateUser({
      ...payload,
      ...(currentAvatar?.type === 'image' && { userImage: currentAvatar.src }),
    });
  }

  async uploadProfilePicture(file: File): Promise<void> {
    await firstValueFrom(this.#uploadProfilePicture$(file));
    void this.updateProfile();
  }

  async deleteProfilePicture(): Promise<void> {
    await firstValueFrom(this.#deleteProfilePicture$());
    this.updateUser({ profilePicture: undefined });
  }

  /** return all web-applications avaible to our user
   * - with basic informations only (e.g. create apps menu)
   * TODO : You may look licenseService for more details about applications (e.g. user application pages / orgs licenses pages)
   */
  async getUserApplications(forceRefresh = false): Promise<ComputedWebApp[]> {
    const userApplications = this.#userApplications();
    if (userApplications && !forceRefresh) return userApplications;

    const applications = await firstValueFrom(this.#fetchUserApplications$());
    const applicationsWithIconHtml = await Promise.all(
      applications.map((app) => this.#CoreClientUtilService.fetchAppIconFromURL(app)),
    );

    this.#userApplications.set(applicationsWithIconHtml);

    return applicationsWithIconHtml;
  }

  checkHasPermission(permission: Permission): boolean {
    const currentUser = this.profile();
    if (!currentUser || !currentUser.permissions) {
      return false;
    }

    return currentUser.permissions?.includes(permission) ?? false;
  }

  // *################### API CALLS OBSERVABLES ###################*

  #fetchUser$(): Observable<ApiUser> {
    return this._configService.safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.user.me).pipe(
      switchMap((uri) => this.#httpClient.get<ApiResponse<ApiUser>>(uri)),
      takeUntilDestroyed(this.#destroyRef),
      map(({ result }) => result),
    );
  }

  #fetchUserPermissions$(): Observable<Permission[]> {
    return this._configService.safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.user.permissions).pipe(
      switchMap((uri) => this.#httpClient.get<ApiResponse<Permission[]>>(uri)),
      takeUntilDestroyed(this.#destroyRef),
      map(({ result }) => result),
    );
  }

  #fetchUserApplications$(): Observable<ApiWebApp[]> {
    return this._configService.safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.user.applications).pipe(
      switchMap((uri) => this.#httpClient.get<ApiResponse<ApiWebApp[]>>(uri)),
      takeUntilDestroyed(this.#destroyRef),
      map(({ result }) => result),
    );
  }

  #saveUser$(payload: ApiSaveUserRequest): Observable<ApiUser> {
    return this._configService.safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.user.me).pipe(
      switchMap((uri) => this.#httpClient.put<ApiResponse<ApiUser>>(uri, payload)),
      takeUntilDestroyed(this.#destroyRef),
      map(({ result }) => result),
    );
  }

  #uploadProfilePicture$(file: File): Observable<boolean> {
    const formData = new FormData();
    formData.append('Picture', file);

    return this._configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.user.profilePicture)
      .pipe(
        switchMap((uri) => this.#httpClient.put<ApiResponse<boolean>>(uri, formData)),
        takeUntilDestroyed(this.#destroyRef),
        map(({ result }) => result),
      );
  }

  #deleteProfilePicture$(): Observable<boolean> {
    return this._configService
      .safelyGetApiUrl$('CORE', CORE_API_ENDPOINTS.user.profilePicture)
      .pipe(
        switchMap((uri) => this.#httpClient.delete<ApiResponse<boolean>>(uri)),
        takeUntilDestroyed(this.#destroyRef),
        map(({ result }) => result),
      );
  }
}
